import React, { useState, useEffect } from 'react';
import { X, Sparkles, BookOpen, Users, Heart, Star, ChevronRight } from 'lucide-react';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName?: string;
}

// Logo URL
const GOLOK_LOGO = 'https://customer-assets.emergentagent.com/job_ef4eb1d2-e6b3-42b0-b647-461b6327fd91/artifacts/qgwuwj0g_IMG_20260212_161028.png';

const WELCOME_FEATURES = [
  {
    icon: <Heart className="text-rose-500" size={20} />,
    title: 'राम नाम जाप',
    description: 'लिखित, स्मरण एवं वाणी से भगवन्नाम का जाप करें'
  },
  {
    icon: <BookOpen className="text-amber-600" size={20} />,
    title: 'दिव्य पुस्तकें',
    description: 'आध्यात्मिक ग्रंथों का अध्ययन करें'
  },
  {
    icon: <Users className="text-blue-500" size={20} />,
    title: 'संघ सत्संग',
    description: 'सामूहिक जाप में सम्मिलित हों'
  },
  {
    icon: <Star className="text-yellow-500" size={20} />,
    title: 'साधना अंतर्दृष्टि',
    description: 'अपनी आध्यात्मिक प्रगति देखें'
  }
];

export const WelcomeModal: React.FC<WelcomeModalProps> = ({ isOpen, onClose, userName }) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  const handleNext = () => {
    if (currentStep < WELCOME_FEATURES.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onClose();
    }
  };

  const handleSkip = () => {
    onClose();
  };
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="welcome-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={handleSkip}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-md bg-white dark:from-stone-800 dark:to-stone-900 rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
        {/* Close Button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors z-10"
          data-testid="welcome-modal-close"
        >
          <X size={20} className="text-gray-500 dark:text-gray-400" />
        </button>
        
        {/* Header with Logo - #FDF5EC background */}
        <div className="bg-[#FDF5EC] p-6 text-center relative overflow-hidden">
          <div className="relative">
            {/* Logo Image */}
            <img 
              src={GOLOK_LOGO} 
              alt="गोलोक दिव्य नाम कोष" 
              className="mx-auto mb-2 h-24 w-auto object-contain drop-shadow-lg"
            />
            <h2 className="text-2xl font-bold text-templeRed font-hindi">
              स्वागत है!
            </h2>
            <p className="text-gray-600 text-sm mt-1">
              {userName ? `प्रिय ${userName},` : 'प्रिय भक्त,'} गोलोक दिव्य नाम कोष में
            </p>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {/* Progress Dots */}
          <div className="flex justify-center gap-2 mb-6">
            {WELCOME_FEATURES.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentStep 
                    ? 'bg-orange-500 w-6' 
                    : index < currentStep 
                      ? 'bg-orange-300' 
                      : 'bg-gray-200 dark:bg-gray-700'
                }`}
              />
            ))}
          </div>
          
          {/* Feature Card */}
          <div className="bg-white dark:bg-stone-800 rounded-2xl p-6 border border-orange-100 dark:border-stone-700 shadow-sm min-h-[180px] flex flex-col items-center justify-center text-center transition-all duration-300">
            <div className="w-14 h-14 rounded-full bg-orange-50 dark:bg-stone-700 flex items-center justify-center mb-4">
              {WELCOME_FEATURES[currentStep].icon}
            </div>
            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 font-hindi mb-2">
              {WELCOME_FEATURES[currentStep].title}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 font-hindi text-sm">
              {WELCOME_FEATURES[currentStep].description}
            </p>
          </div>
          
          {/* Actions */}
          <div className="mt-6 space-y-3">
            <button
              onClick={handleNext}
              className="w-full py-4 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold rounded-2xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-orange-500/20"
              data-testid="welcome-modal-next"
            >
              {currentStep < WELCOME_FEATURES.length - 1 ? (
                <>
                  आगे बढ़ें <ChevronRight size={20} />
                </>
              ) : (
                <>
                  जाप शुरू करें <Sparkles size={20} />
                </>
              )}
            </button>
            
            {currentStep < WELCOME_FEATURES.length - 1 && (
              <button
                onClick={handleSkip}
                className="w-full py-3 text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 font-medium text-sm transition-colors"
                data-testid="welcome-modal-skip"
              >
                छोड़ें
              </button>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 pb-4 text-center">
          <p className="text-xs text-gray-400 dark:text-gray-500 font-hindi">
            जय श्री राम 🙏
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
